import { useCallback, useState } from 'react';
import * as React from 'react';
import { useLocation } from '@reach/router';
import { useWindowWidth } from '@react-hook/window-size';
import { GatsbyImage } from 'gatsby-plugin-image';
import { darken, rgba } from 'polished';
import styled, { css } from 'styled-components';

import {
  createUseCaseCategoryLink,
  createUseCaseLink,
  navigate
} from '../../utils';
import { Heading, Text } from '../design-system';
import { type UseCaseItem } from '../use-cases/UseCaseGridItem';

const MAX_TOOLTIP_WIDTH = 300;

const Top = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 30px;
  transition: transform ${({ theme }) => theme.animation.DURATION}
    ${({ theme }) => theme.animation.EASING};
`;

const TemplateCard = styled.div`
  flex: 1 1 auto;
  height: 50px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.1);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-shadow: 0 0 0 1px ${({ theme }) => rgba(theme.borderColor.DEFAULT, 0.1)};
  transition: box-shadow ${({ theme }) => theme.animation.DURATION}
    ${({ theme }) => theme.animation.EASING};

  &:hover {
    box-shadow: 0 0 0 1px ${({ theme }) => rgba(theme.borderColor.DEFAULT, 0.4)};
  }

  [data-gatsby-image-wrapper] {
    position: absolute;
    width: 300%;
    filter: invert(0.94);
  }
`;

const Templates = styled.div<{ $orientation: 'horizontal' | 'wrap' }>`
  gap: 10px;
  padding: 0 30px 30px;
  transition: transform ${({ theme }) => theme.animation.DURATION}
    ${({ theme }) => theme.animation.EASING};

  ${({ $orientation }) =>
    $orientation === 'horizontal' &&
    css`
      display: flex;

      ${TemplateCard} {
        height: 130px;
      }
    `}

  ${({ $orientation }) =>
    $orientation === 'wrap' &&
    css`
      display: grid;
      grid-template-columns: 1fr 1fr;

      ${TemplateCard} {
        height: 90px;
      }
    `}
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 3px;
  background: rgba(26, 26, 35, 0.7);
  box-shadow:
    0 0 0 1px ${({ theme }) => rgba(theme.borderColor.DEFAULT, 0.25)},
    0px 4px 50px 0px rgba(0, 0, 0, 0.52);
  cursor: pointer;

  transition: box-shadow ${({ theme }) => theme.animation.DURATION}
    ${({ theme }) => theme.animation.EASING};

  &:hover {
    box-shadow:
      0 0 0 1px ${({ theme }) => rgba(theme.highlightColor, 0.3)},
      0px 4px 50px 0px rgba(0, 0, 0, 0.52);

    ${Top} {
      transform: translateY(-2px);
    }

    ${Templates} {
      transform: scale(1.02);
    }
  }
`;

const HoverState = styled.div<{
  $mousePosition: MousePosition | null;
  $visible: boolean;
}>`
  position: fixed;
  background: ${({ theme }) => darken(0.1, theme.backgroundColor)};
  color: ${({ theme }) => theme.fontColor.LOUD};
  border-radius: 3px;
  padding: 10px 12px;
  top: 0;
  left: 0;
  opacity: 0;
  max-width: ${MAX_TOOLTIP_WIDTH}px;
  z-index: 999999;
  pointer-events: none;
  transition: opacity ${({ theme }) => theme.animation.DURATION}
    ${({ theme }) => theme.animation.EASING};

  > div {
    line-height: 1.4;
  }

  > div:nth-child(2) {
    opacity: 0.7;
    margin-top: 3px;
  }

  ${({ $visible }) =>
    $visible &&
    css`
      opacity: 1;
    `}

  ${({ $mousePosition }) =>
    $mousePosition &&
    css`
      transform: translateX(${$mousePosition.x + 10}px)
        translateY(${$mousePosition.y + 10}px);
    `}
`;

export interface BuildAnythingCardType {
  description: {
    description: string;
  };
  slug: string;
  title: string;
  use_case?: UseCaseItem[];
}

interface MousePosition {
  x: number;
  y: number;
}

interface TooltipType {
  description?: string;
  title: string;
}

export const BuildAnythingCard: React.ComponentType<BuildAnythingCardType> =
  React.memo(({ description, use_case, title, slug }) => {
    const { search } = useLocation();
    const viewportWidth = useWindowWidth();

    const [tooltipContent, setTooltipContent] = useState<TooltipType | null>(
      null
    );
    const [isShowingTooltip, setIsShowingTooltip] = useState<boolean>(false);
    const [mousePosition, setMousePosition] = useState<MousePosition | null>(
      null
    );

    const onMouseEnterCard = useCallback((title: string) => {
      setTooltipContent({ title: `View all ${title} templates` });
      setIsShowingTooltip(true);
    }, []);

    const onMouseLeave = useCallback(() => {
      setIsShowingTooltip(false);
    }, []);

    const onMouseEnterTemplate = useCallback((template: UseCaseItem) => {
      setTooltipContent({
        title: template.name,
        description: template.seoMetaDescription
      });
      setIsShowingTooltip(true);
    }, []);

    const onMouseMove = useCallback(
      (e: React.MouseEvent) => {
        const xPos =
          e.clientX + MAX_TOOLTIP_WIDTH >= viewportWidth
            ? e.clientX - MAX_TOOLTIP_WIDTH
            : e.clientX;
        setMousePosition({ x: xPos, y: e.clientY });
      },
      [viewportWidth]
    );

    const templatesToShow =
      use_case && use_case.length > 6 ? use_case.slice(0, 6) : use_case;

    return (
      <Wrapper
        onClick={() => {
          navigate(createUseCaseCategoryLink({ slug }), search);
        }}>
        <Top
          onMouseEnter={() => onMouseEnterCard(title)}
          onMouseLeave={onMouseLeave}
          onMouseMove={onMouseMove}>
          <Heading renderAs="h4">{title}</Heading>
          <Text size="default">{description.description}</Text>
        </Top>
        <Templates
          $orientation={templatesToShow?.length === 3 ? 'horizontal' : 'wrap'}>
          {templatesToShow?.map((item, index) => (
            <TemplateCard
              key={index}
              onClick={e => {
                e.stopPropagation();
                navigate(createUseCaseLink(item), search);
              }}
              onMouseEnter={() => onMouseEnterTemplate(item)}
              onMouseLeave={onMouseLeave}
              onMouseMove={onMouseMove}>
              <GatsbyImage
                alt={item.gridImage.title}
                image={item.gridImage.gatsbyImageData}
                sizes="300px"
              />
            </TemplateCard>
          ))}
        </Templates>
        <HoverState $mousePosition={mousePosition} $visible={isShowingTooltip}>
          <div>{tooltipContent?.title}</div>
          {tooltipContent?.description && (
            <div>{tooltipContent?.description}</div>
          )}
        </HoverState>
      </Wrapper>
    );
  });
